import React from 'react';
import { arrayOf, oneOf, string, shape } from 'prop-types';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import css from './ListingBadges.module.css';

/**
 * Badge variants.
 * Add more variants by extending this object and the CSS.
 */
export const BADGE_VARIANTS = {
  new: { labelKey: 'ListingBadges.new', className: css.badgeNew },
  featured: { labelKey: 'ListingBadges.featured', className: css.badgeFeatured },
  sale: { labelKey: 'ListingBadges.sale', className: css.badgeSale },
  soldOut: { labelKey: 'ListingBadges.soldOut', className: css.badgeSoldOut },
  popular: { labelKey: 'ListingBadges.popular', className: css.badgePopular },
  verified: { labelKey: 'ListingBadges.verified', className: css.badgeVerified },
};

/**
 * ListingBadges — renders badge chips on a listing card or page.
 *
 * Badges are stored in listing.attributes.publicData.badges[]
 * as an array of badge keys, e.g. ['new', 'featured'].
 *
 * Can also receive custom badge objects: [{ key: 'custom', label: 'Custom' }].
 *
 * @param {string[]} props.badges — array of badge keys or objects
 * @param {string}   [props.className]
 * @param {string}   [props.size] — 'sm' | 'md' (default: 'sm')
 */
const ListingBadges = ({ badges, className, size }) => {
  const intl = useIntl();

  if (!badges || badges.length === 0) return null;

  return (
    <div className={classNames(css.root, className)}>
      {badges.map((badge, i) => {
        const key = typeof badge === 'string' ? badge : badge.key;
        const variant = BADGE_VARIANTS[key];
        const label =
          typeof badge === 'object' && badge.label
            ? badge.label
            : variant
            ? intl.formatMessage({ id: variant.labelKey })
            : key;

        return (
          <span
            key={`${key}-${i}`}
            className={classNames(
              css.badge,
              variant?.className || css.badgeDefault,
              size === 'md' ? css.badgeMd : css.badgeSm
            )}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
};

ListingBadges.defaultProps = {
  badges: [],
  className: null,
  size: 'sm',
};

ListingBadges.propTypes = {
  badges: arrayOf(
    oneOf([string, shape({ key: string, label: string })])
  ),
  className: string,
  size: oneOf(['sm', 'md']),
};

export default ListingBadges;
